import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Edit2, Trash2, AlertTriangle, Calendar, Globe, Eye, Play, X } from 'lucide-react';
import { type CustomAdventure, updateCustomAdventure } from '@/utils/businessAppApi';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { InteractiveAdventurePreview } from './adventure-builder/components/InteractiveAdventurePreview';

// Import design system tokens
import { getCardClasses } from '@/components/business/design-system/businessDesignTokens';

interface CustomAdventuresListProps {
  adventures: CustomAdventure[];
  loading: boolean;
  error?: string | null;
  onEdit: (adventure: CustomAdventure) => void;
  onDelete: (id: string) => void;
  onCreateAdventure?: () => void;
  deletingId?: string | null;
  onAdventureUpdated?: () => void;
}

export function CustomAdventuresList({ 
  adventures, 
  loading, 
  error, 
  onEdit, 
  onDelete,
  onCreateAdventure,
  deletingId,
  onAdventureUpdated
}: CustomAdventuresListProps) {
  // State for managing UI interactions
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [adventureToDelete, setAdventureToDelete] = useState<string | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [adventureToPreview, setAdventureToPreview] = useState<CustomAdventure | null>(null);
  
  // Debug logging
  console.log('🔍 [CustomAdventuresList] Received props:', {
    adventures: adventures,
    adventuresLength: adventures?.length || 0,
    loading,
    error,
    adventuresArray: Array.isArray(adventures),
    firstAdventureName: adventures?.[0]?.name || 'N/A',
    firstAdventureId: adventures?.[0]?.id || 'N/A'
  });
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
        <span className="text-primary font-medium">Loading your custom adventures...</span>
        <p className="text-gray-500 text-sm">Please wait while we fetch your adventures</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 bg-red-50 rounded-lg border border-red-200 space-y-4">
        <AlertTriangle className="h-12 w-12 text-red-500" />
        <span className="text-red-700 font-medium text-lg">Unable to load adventures</span>
        <p className="text-red-600 text-sm max-w-md text-center">{error}</p>
        <Button onClick={() => window.location.reload()} variant="destructive" className="mt-2">
          Try Again
        </Button>
      </div>
    );
  }

  // Safety check: ensure adventures is an array and handle edge cases
  const safeAdventures = Array.isArray(adventures) ? adventures : [];
  console.log('🔍 [CustomAdventuresList] Safe adventures array:', safeAdventures);
  
  console.log('📋 [CustomAdventuresList] Component render decision:', {
    willShowLoading: loading,
    willShowError: !!error,
    willShowEmpty: !safeAdventures.length && !loading && !error,
    willShowAdventures: safeAdventures.length > 0 && !loading && !error,
    safeAdventuresLength: safeAdventures.length
  });
  
  if (!safeAdventures.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
        <div className="bg-white p-3 rounded-full shadow-sm mb-4">
          <Calendar className="h-8 w-8 text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">No custom adventures yet</h3>
        <p className="text-gray-500 mb-6 max-w-md">
          Create your first custom adventure to offer personalized experiences to your customers.
        </p>
        <Button 
          className="bg-primary hover:bg-[#224924]" 
          size="lg"
          onClick={onCreateAdventure}
        >
          Create Your First Adventure
        </Button>
      </div>
    );
  }

  // Handle delete confirmation
  const handleDeleteClick = (id: string) => {
    setAdventureToDelete(id);
  };

  const confirmDelete = () => {
    if (adventureToDelete) {
      onDelete(adventureToDelete);
      setAdventureToDelete(null);
    }
  };

  // Handle publish/unpublish toggle
  const handlePublishToggle = async (adventure: CustomAdventure) => {
    if (publishingId) return; // Prevent multiple simultaneous updates
    
    setPublishingId(adventure.id);
    
    try {
      const updatedAdventure = await updateCustomAdventure(adventure.id, {
        published: !adventure.published
      });
      
      console.log('✅ Adventure publication status updated:', {
        id: adventure.id,
        name: adventure.name,
        previousStatus: adventure.published,
        newStatus: updatedAdventure.published
      });
      
      // Trigger refresh of the adventures list if callback is provided
      if (onAdventureUpdated) {
        onAdventureUpdated();
      }
      
    } catch (error) {
      console.error('❌ Failed to update adventure publication status:', error);
      // You could add a toast notification here for better UX
    } finally {
      setPublishingId(null);
    }
  };

  // Handle preview modal
  const handlePreviewClick = (adventure: CustomAdventure) => {
    setAdventureToPreview(adventure);
    setPreviewModalOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewModalOpen(false);
    setAdventureToPreview(null);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence mode="popLayout">
          {safeAdventures.map((adventure) => (
            <motion.div
              key={adventure.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className={cn(getCardClasses('basic'), "overflow-hidden flex flex-col")}
            >
              {/* Status banner */}
              <div className={cn(
                "h-2 w-full relative",
                adventure.published ? "bg-green-500" : "bg-gray-400"
              )}>
                <div className={cn(
                  "absolute inset-0 bg-gradient-to-r opacity-30",
                  adventure.published ? "from-green-400 to-green-600" : "from-gray-300 to-gray-500"
                )} />
              </div>
              
              <div className="p-4 flex flex-col h-full">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-lg text-gray-900 line-clamp-1">{adventure.name}</h3>
                  
                  <TooltipProvider>
                    <div className="flex gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handlePreviewClick(adventure)}
                            className="text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                            aria-label="Preview adventure"
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-white text-black">
                          <p>Preview adventure form</p>
                        </TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onEdit(adventure)}
                            className="text-gray-500 hover:text-primary hover:bg-green-50"
                            aria-label="Edit adventure"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-white text-black">
                          <p>Edit adventure details</p>
                        </TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteClick(adventure.id)}
                            className="text-gray-500 hover:text-red-600 hover:bg-red-50"
                            disabled={deletingId === adventure.id}
                            aria-label="Delete adventure"
                          >
                            {deletingId === adventure.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-white text-black">
                          <p>{deletingId === adventure.id ? 'Deleting...' : 'Delete adventure permanently'}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TooltipProvider>
                </div>
                
                <p className="text-gray-500 text-sm mb-4 line-clamp-2">{adventure.description}</p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {adventure.options.slice(0, 3).map((opt, idx) => (
                    <Badge
                      key={idx}
                      variant="outline"
                      className="bg-gray-50 text-gray-700 rounded-full"
                    >
                      {opt.label}
                    </Badge>
                  ))}
                  {adventure.options.length > 3 && (
                    <Badge variant="outline" className="bg-gray-50 text-gray-700 rounded-full">
                      +{adventure.options.length - 3} more
                    </Badge>
                  )}
                </div>
                
                <div className="flex justify-between items-center border-t pt-4 mt-auto">
                  <div className="flex gap-2 items-center">
                    <Badge 
                      variant={adventure.published ? "default" : "secondary"}
                      className={cn(
                        "rounded-lg",
                        adventure.published 
                          ? "bg-green-100 text-green-700 border-green-200" 
                          : "bg-gray-100 text-gray-700 border-gray-200"
                      )}
                    >
                      {adventure.published ? "Published" : "Draft"}
                    </Badge>
                    
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant={adventure.published ? "outline" : "default"}
                          onClick={() => handlePublishToggle(adventure)}
                          disabled={publishingId === adventure.id || deletingId === adventure.id}
                          className={cn(
                            "text-xs transition-all duration-200",
                            adventure.published
                              ? "border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400"
                              : "bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 text-white border-blue-600"
                          )}
                        >
                          {publishingId === adventure.id ? (
                            <div className="flex items-center gap-1">
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current" />
                              <span>Updating...</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              {adventure.published ? (
                                <>
                                  <Eye className="h-3 w-3" />
                                  <span>Unpublish</span>
                                </>
                              ) : (
                                <>
                                  <Globe className="h-3 w-3" />
                                  <span>Publish</span>
                                </>
                              )}
                            </div>
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-white text-black">
                        <p>
                          {publishingId === adventure.id 
                            ? (adventure.published ? 'Making adventure private...' : 'Making adventure public...') 
                            : (adventure.published 
                                ? 'Click to make this adventure private (clients won\'t see it)' 
                                : 'Click to publish this adventure (make it visible to clients)'
                              )
                          }
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  </div>
                  
                  <span className="ml-2 text-gray-400 text-xs">
                    Created: {format(new Date(), 'MMM d, yyyy')}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Delete Confirmation Modal */}
      <AlertDialog open={!!adventureToDelete} onOpenChange={() => setAdventureToDelete(null)}>
        <AlertDialogContent className="border-slate-200/60 shadow-xl shadow-slate-900/10">
          <AlertDialogHeader className="pb-6 border-b border-slate-100/80">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-100 to-red-200 border border-red-200/50 flex items-center justify-center shadow-sm">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <AlertDialogTitle className="text-xl font-semibold text-slate-900">
                  Delete Custom Adventure
                </AlertDialogTitle>
                <AlertDialogDescription className="text-slate-600 mt-1">
                  This action cannot be undone
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          
          <div className="py-6">
            <div className="p-4 rounded-lg bg-red-50 border border-red-200/50">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-900 mb-1">
                    Delete this custom adventure?
                  </p>
                  <p className="text-sm text-red-700">
                    This will permanently remove the adventure and all its configurations including pricing rules and options. 
                    Any existing customer bookings will not be affected.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <AlertDialogFooter className="pt-6 border-t border-slate-200/60">
            <AlertDialogCancel 
              onClick={() => setAdventureToDelete(null)}
              className="border-slate-200/80 shadow-sm hover:shadow-md transition-shadow"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete} 
              className="bg-red-600 hover:bg-red-700 shadow-md hover:shadow-lg transition-shadow"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Delete Adventure
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Modal */}
      <Dialog open={previewModalOpen} onOpenChange={handleClosePreview}>
        <DialogContent className="max-w-7xl max-h-[95vh] overflow-y-auto border-slate-200/60 shadow-xl shadow-slate-900/10 p-0">
          <DialogHeader className="px-6 py-6 border-b border-slate-200/60 bg-gradient-to-r from-blue-50 to-blue-100/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
                  <Play className="w-6 h-6 text-white" />
                </div>
                <div>
                  <DialogTitle className="text-2xl font-bold text-slate-900">
                    Adventure Preview
                  </DialogTitle>
                  <p className="text-slate-600 mt-1">
                    Experience "{adventureToPreview?.name}" as your customers will see it
                  </p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleClosePreview}
                className="text-slate-500 hover:text-slate-700 hover:bg-white/50"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </DialogHeader>
          
          <div className="p-6 bg-white">
            <div className="max-w-6xl mx-auto">
              {/* Preview Notice */}
              <div className="mb-6 p-4 rounded-lg bg-blue-50 border border-blue-200/50">
                <div className="flex items-start gap-3">
                  <Eye className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-900 mb-1">
                      Interactive Preview Mode
                    </p>
                    <p className="text-sm text-blue-700">
                      This is exactly how your customers will experience this adventure form, including live pricing calculations.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Adventure Preview Content */}
              {adventureToPreview && (
                <div className="bg-slate-50/50 rounded-xl p-6 border border-slate-200/60">
                  <InteractiveAdventurePreview
                    adventureName={adventureToPreview.name}
                    adventureDescription={adventureToPreview.description}
                    basePrice={adventureToPreview.basePrice}
                    basePriceType={adventureToPreview.basePriceType || 'fixed'}
                    currency={adventureToPreview.currency}
                    options={adventureToPreview.options.map(opt => ({
                      label: opt.label,
                      type: opt.type,
                      required: opt.required,
                      options: opt.options,
                      pricing: opt.pricing,
                      isMultiplier: opt.isMultiplier,
                      multiplierTarget: opt.multiplierTarget,
                      selectionMode: opt.selectionMode
                    }))}
                    showHeader={false}
                    className="max-w-none"
                  />
                </div>
              )}
              
              {/* Close Button */}
              <div className="mt-6 flex justify-center">
                <Button
                  onClick={handleClosePreview}
                  className="bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 px-8 shadow-md hover:shadow-lg transition-shadow"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Close Preview
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
} 